import type { HTMLAttributes, PropsWithChildren } from 'react';
import { cn } from 'utils/cn';
import { useTabsContext } from './Tabs';

export interface TabPanelProps extends PropsWithChildren<
  HTMLAttributes<HTMLDivElement>
> {
  index?: number;
  forceRender?: boolean;
}

function TabPanel({
  children,
  className,
  index,
  forceRender = false,
  ...props
}: TabPanelProps) {
  const { selectedIndex, baseId } = useTabsContext();
  const isSelected = index === selectedIndex;
  // Safari's accessibility tree still listed a display:none panel that its tab
  // names via aria-labelledby, so VoiceOver read an unselected panel's title
  // after the visible panel (Mac-App #2676). aria-hidden keeps it out.
  const isHiddenPanel = forceRender && !isSelected;

  if (!forceRender && !isSelected) {
    return null;
  }

  return (
    <div
      role='tabpanel'
      id={`${baseId}-panel-${index}`}
      aria-labelledby={`${baseId}-tab-${index}`}
      hidden={isHiddenPanel}
      aria-hidden={isHiddenPanel}
      className={cn(isHiddenPanel && 'hidden', className)}
      {...props}>
      {children}
    </div>
  );
}

TabPanel.displayName = 'UIKitTabPanel';

export { TabPanel };
